import React from 'react';
import {Avatar, Box, Chip, Stack, Typography} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {generateUserColor} from '../../../components/prosemirror-editor/helpers/utils/users';
import {formatTimestamp} from '../../../utils/formatTimestamp';
import UserAvatar from '../../../components/UserAvatar';

// AI Author configuration
const AI_AUTHOR = {
    uid: 'ai',
    displayName: 'AI Assistant',
    isAI: true
};

/**
 * Component to display disclosure-specific metadata
 * Similar to ReportMetadata but tailored for disclosure documents
 */
const DisclosureMetadata = ({meta, previewAuthors, reportDoc, version, latestVersion, selectedAuthorId, onAuthorClick}) => {
    const authors = Array.isArray(previewAuthors) && previewAuthors.length > 0
        ? previewAuthors
        : (meta?.authors || []);

    // Disclosure-specific fields
    const disclosureType = meta?.disclosureType;
    const disclosureTypeLabel = disclosureType?.label || disclosureType?.value || 'N/A';
    const isNDP1 = disclosureType?.value === 'NDP-1';
    const selectedCountries = meta?.selectedCountries || '';
    const purpose = meta?.purpose;
    const purposeLabel = purpose?.label || purpose?.value || 'N/A';
    const displayOnly = meta?.displayOnly || false;

    // Compute classification marking for NDP-1
    const computedClassification = isNDP1 && selectedCountries
        ? (displayOnly ? `DISPLAY ONLY ${selectedCountries.toUpperCase()}` : `REL TO ${selectedCountries.toUpperCase()}`)
        : '';

    return (
        <Stack spacing={1} p={2} pt={1} bgcolor={'#f9f9f9'} borderRadius={1} mb={2}>
            {/* Parent Product Link */}
            {(reportDoc?.parent_product || reportDoc?.parentReportId) && (
                <Typography variant="subtitle2">
                    Parent
                    Product: {reportDoc?.parent_product?.label || reportDoc?.parent_product?.uid || reportDoc?.parentReportId}
                </Typography>
            )}

            {/* Authors */}
            {authors.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600}}>
                        Authors:
                    </Typography>
                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                        {authors.map((author) => {
                            const isAI = author.uid === 'ai' || author.isAI;
                            const isSelected = selectedAuthorId === author.uid;
                            const userColor = generateUserColor(author.uid);

                            return (
                                <Chip
                                    key={author.uid}
                                    avatar={
                                        isAI ? (
                                            <Avatar sx={{bgcolor: 'white'}}>
                                                <SmartToyIcon sx={{fontSize: 16}}/>
                                            </Avatar>
                                        ) : (
                                            <UserAvatar
                                                user={author}
                                                userId={author.uid}
                                                sx={{width: 24, height: 24, fontSize: 12}}
                                            />
                                        )
                                    }
                                    label={author.displayName || (isAI ? AI_AUTHOR.displayName : 'Unknown')}
                                    onClick={() => onAuthorClick?.(author.uid)}
                                    clickable
                                    variant={isSelected ? 'filled' : 'outlined'}
                                    sx={{
                                        borderColor: isSelected ? userColor : undefined,
                                        bgcolor: isSelected ? `${userColor}22` : undefined,
                                        borderWidth: isSelected ? 2 : 1,
                                        '&:hover': {
                                            bgcolor: `${userColor}33`,
                                        }
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Disclosure Type */}
            {disclosureType && (
                <Typography variant="subtitle2">
                    Disclosure Type: {disclosureTypeLabel}
                </Typography>
            )}

            {/* NDP-1 specific fields */}
            {isNDP1 && (
                <>
                    {selectedCountries && (
                        <Typography variant="subtitle2">
                            Country: {selectedCountries}
                        </Typography>
                    )}
                    {purpose && (
                        <Typography variant="subtitle2">
                            Purpose: {purposeLabel}
                        </Typography>
                    )}
                    <Typography variant="subtitle2">
                        Display Only: {displayOnly ? 'Yes' : 'No'}
                    </Typography>
                    {computedClassification && (
                        <Typography variant="subtitle2" sx={{fontWeight: 600, color: 'primary.main'}}>
                            Classification Marking: {computedClassification}
                        </Typography>
                    )}
                </>
            )}

            {/* DTG */}
            <Typography variant="subtitle2">
                DTG (UTC): {meta?.dtgIso ? formatTimestamp(meta.dtgIso) : (meta?.dtg || 'N/A')}
            </Typography>

            {/*/!* Classification *!/*/}
            {/*<Typography variant="subtitle2">*/}
            {/*    Classification: {meta?.classification?.label || meta?.classification?.value || 'N/A'}*/}
            {/*</Typography>*/}

            {/* Distribution */}
            {/*<Typography variant="subtitle2">*/}
            {/*    Distribution: {distributionText}*/}
            {/*</Typography>*/}

            {/*/!* Compartment *!/*/}
            {/*{compartment.length > 0 && (*/}
            {/*    <Typography variant="subtitle2">*/}
            {/*        Compartment: {compartmentText}*/}
            {/*    </Typography>*/}
            {/*)}*/}

            {/*/!* Product Type *!/*/}
            {/*{meta?.productType && (*/}
            {/*    <Typography variant="subtitle2">*/}
            {/*        Product Type: {meta.productType.label || meta.productType.value || 'N/A'}*/}
            {/*    </Typography>*/}
            {/*)}*/}

            {/*/!* Originator *!/*/}
            {/*<Typography variant="subtitle2">*/}
            {/*    Originator: {meta?.originator?.label || meta?.originator?.value || 'Unknown'}*/}
            {/*</Typography>*/}

            {/*/!* Production Center *!/*/}
            {/*<Typography variant="subtitle2">*/}
            {/*    Production Center: {meta?.productionCenter?.label || meta?.productionCenter?.value || 'Unknown'}*/}
            {/*</Typography>*/}

            {/*/!* Version *!/*/}
            {/*<Typography variant="subtitle2">*/}
            {/*    Version: {version}{version === latestVersion ? ' (latest)' : ''}*/}
            {/*</Typography>*/}

            {/*/!* Status *!/*/}
            {/*{reportDoc?.status && (*/}
            {/*    <Typography variant="subtitle2">*/}
            {/*        Status: {reportDoc.status}*/}
            {/*    </Typography>*/}
            {/*)}*/}
        </Stack>
    );
};

export default DisclosureMetadata;
